/**
 * Affichage d'une personne du registre (team_members) dans les documents.
 *
 * Le contrat est celui du rider/day sheet, documenté et testé :
 *   - NOM  = `nickname` s'il est renseigné, sinon `name`. Un document de tournée
 *     imprime le nom de scène (« Xy »), pas l'état civil (« Alexandre Locher »).
 *   - POSITION = `role` s'il est renseigné, sinon le PREMIER tag qui n'est pas la
 *     catégorie (« mon », « foh », « tm »…). Jamais `category` : elle est un miroir
 *     du 1er tag, pas une position, et l'afficher produirait « technician » pour
 *     tout le monde. Jamais de repli `|| 'crew'` : un poste inconnu reste vide,
 *     une chaîne inventée est pire qu'une cellule vide.
 *
 * Ces helpers sont VOLONTAIREMENT locaux aux documents : les pages de liste
 * (CrewListPage / MusicianListPage) gardent leurs propres libellés, on ne les
 * touche pas — un changement ici ne doit pas modifier ce qu'elles affichent.
 */
export interface PersonLike {
  name?: string | null;
  nickname?: string | null;
  role?: string | null;
  tags?: string[] | null;
  category?: string | null;
  /** Identité Passeport (2026-11-03) : `real_name` = prénom(s), `last_name` = nom.
   *  C'est ce que l'hôtel, la compagnie et le bateau lisent — jamais le nom de scène. */
  real_name?: string | null;
  last_name?: string | null;
}

const clean = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** Nom imprimé sur un document : nickname sinon name. */
export function personDisplayName(p: PersonLike | null | undefined): string {
  if (!p) return '';
  return clean(p.nickname) || clean(p.name);
}

/**
 * Nom OFFICIEL (passeport) pour les documents qui engagent une réservation :
 * rooming list, vols, listes d'embarquement. Format d'usage des bookings :
 * `NOM, Prénom` — le nom de famille en capitales, le prénom tel que saisi
 * (accents préservés : « VERGÈRES, Johann » ne devient pas « VERGERES »).
 *
 * Replis, dans l'ordre : real_name+last_name → name → nickname → ''. On ne
 * fabrique JAMAIS un nom : une réservation au nom de scène est précisément la
 * « surprise » que cette fonction existe pour éviter.
 */
export function personOfficialName(p: PersonLike | null | undefined): string {
  if (!p) return '';
  const last = clean(p.last_name);
  const first = clean(p.real_name);
  if (last && first) return `${last.toUpperCase()}, ${first}`;
  if (last) return last.toUpperCase();
  if (first) return first;
  return clean(p.name) || clean(p.nickname);
}

/**
 * Fonctions imprimées d'une personne (2026-11-03, corrigé 2026-11-04).
 *
 * ⛔ Le `role` de la carte ET ses `tags` sont TOUS DEUX des fonctions. La carte
 * d'équipe range la fonction principale dans `role` et les fonctions
 * supplémentaires dans `tags` : Chopiou a `role = "mon"` et
 * `tags = ["production manager"]`. Une première version n'imprimait le `role`
 * QUE si la carte n'avait aucun tag → « production manager » tout seul, et le
 * `mon` disparaissait (vécu : « il devrait être mon + production manager »).
 * `personPosition` documentait pourtant déjà « le role d'abord, puis les tags » :
 * l'implémentation avait dérivé du contrat. Cette fabrique est désormais
 * l'UNIQUE source de l'ordre et du dédoublonnage.
 *
 * Règles :
 *   - ordre = `role` d'abord (fonction principale), puis les tags ;
 *   - synonymes hérités ramenés au vocabulaire du groupe (`sound` → `foh`,
 *     `monitor` → `mon`, `ld`/`light` → `lights`, `tm` → `tour manager`) : c'est
 *     ce qui évite « LD, lights » (la même fonction sous deux vocabulaires) ;
 *   - dédoublonnage casse-insensible ;
 *   - les valeurs de CLASSE (`crew`, `tech`, `musician`, la `category`) ne sont
 *     jamais des fonctions : elles sont écartées.
 */
const CREW_TAG_ALIASES: Record<string, string> = {
  sound: 'foh',
  'sound engineer': 'foh',
  'foh engineer': 'foh',
  audio: 'foh',
  monitor: 'mon',
  'monitor engineer': 'mon',
  light: 'lights',
  ld: 'lights',
  lighting: 'lights',
  'lighting technician': 'lights',
  tour: 'tour manager',
  tourmanager: 'tour manager',
  tm: 'tour manager',
};

/** Ramène un tag hérité au vocabulaire du groupe (sinon le rend tel quel). */
export function canonicalCrewTag(tag: string): string {
  const v = clean(tag);
  if (!v) return '';
  return CREW_TAG_ALIASES[v.toLowerCase()] || v;
}

/** Valeurs qui nomment une CLASSE (catégorie, rôle générique), jamais une fonction. */
const CLASS_VALUES = new Set(['crew', 'tech', 'musician', 'session', 'main']);

/**
 * Deux libellés disent-ils la SAME fonction sous deux vocabulaires ?
 * « Production Assistant » (role d'une carte héritée) et le tag `prod` en sont
 * un exemple : imprimer « Production Assistant + prod » serait la redite que
 * l'ancienne règle évitait. Test : un libellé est une ABRÉVIATION de l'autre
 * quand l'un est le préfixe (≥ 3 lettres) d'un mot de l'autre.
 * « mon » et « production manager » ne se préfixent pas → deux fonctions.
 */
function sameFunction(a: string, b: string): boolean {
  const x = a.toLowerCase();
  const y = b.toLowerCase();
  if (x === y) return true;
  const prefixOf = (short: string, long: string): boolean =>
    short.length >= 3 &&
    long.split(/[^a-z0-9]+/).some((w) => w.length >= short.length && w.startsWith(short));
  return prefixOf(x, y) || prefixOf(y, x);
}

export function personFunctions(p: PersonLike | null | undefined): string[] {
  if (!p) return [];
  const cat = clean(p.category).toLowerCase();
  const out: string[] = [];
  const push = (raw: string) => {
    const v = canonicalCrewTag(raw);
    if (!v) return;
    const key = v.toLowerCase();
    if (CLASS_VALUES.has(key) || key === cat) return;
    if (out.some((x) => sameFunction(x, key))) return;
    out.push(v);
  };
  // ORDRE : les TAGS d'abord — ce sont les FONCTIONS du groupe (« mon »,
  // « production manager », « foh », « lights », « guitar tech ») et c'est ce
  // que l'utilisateur lit comme la vérité (« Olle est foh, son tag » ; « Chopiou
  // a 2 tags avec SAMAEL »). Le `role` de la carte n'est qu'un repli quand la
  // carte n'a AUCUN tag. Une carte dont les tags sont INCOMPLETS n'est pas
  // rattrapée ici : on corrige la DONNÉE, pas l'affichage.
  const tags = (p.tags || []).map(clean).filter(Boolean);
  for (const tg of tags) push(tg);
  if (out.length === 0) push(clean(p.role));
  return out;
}

/**
 * Position(s) imprimée(s) : **TOUTES** les fonctions, jointes par « , ».
 * Une personne peut cumuler des rôles — le tour book de Håkon imprime
 * « Mon, production manager », pas seulement le premier.
 * La catégorie (`musician` / `crew` / `tech`) n'est JAMAIS imprimée : c'est un
 * classeur, pas une fonction.
 */
export function personPosition(p: PersonLike | null | undefined): string {
  return capitalizeFirst(personFunctions(p).join(', '));
}

/** Première lettre en majuscule (« mon, production manager » → « Mon, production manager »). */
export function capitalizeFirst(s: string): string {
  const v = (s || '').trim();
  if (!v) return '';
  return v.charAt(0).toUpperCase() + v.slice(1);
}

/** Une ligne « BAND AND CREW » prête à imprimer. */
export interface CrewRow {
  name: string;
  position: string;
}

/**
 * Résout les personnes d'une date à partir des affectations (`date_assignments`,
 * rendues par l'API sous la forme `rooming`) + le registre de l'équipe.
 *
 * ORDRE (demande utilisateur 2026-11-03) : les MUSICIENS d'abord, puis les autres
 * (« crew » / tech / inconnus), chaque groupe trié par NOM AFFICHÉ (A→Z, insensible
 * à la casse et aux accents). C'est l'ordre d'une page « band and crew » de tour
 * manager : le groupe, puis l'équipe.
 *
 * Une affectation sans fiche connue retombe sur son libellé de date, jamais sur
 * un UUID.
 */
export function crewRowsFromAssignments(
  assignments: Array<{ team_member_id?: string | number | null; role_at_date?: string | null }> | null | undefined,
  members: Array<PersonLike & { id?: string | number | null }> | null | undefined,
): CrewRow[] {
  const seen = new Set<string>();
  const list = members || [];
  const byId = new Map<string, PersonLike & { id?: string | number | null }>();
  for (const m of list) if (m && m.id != null) byId.set(String(m.id), m);

  // Ordre : registre (déjà trié par nom côté serveur) puis affectations inconnues.
  const ordered: Array<{ team_member_id?: string | number | null; role_at_date?: string | null }> = [];
  const assignedIds = new Set((assignments || []).map((a) => String(a.team_member_id)));
  for (const m of list) if (m && m.id != null && assignedIds.has(String(m.id))) ordered.push({ team_member_id: m.id });
  for (const a of assignments || []) if (!byId.has(String(a.team_member_id))) ordered.push(a);

  const sortable: Array<CrewRow & { rank: number }> = [];
  for (const a of ordered) {
    const id = String(a.team_member_id ?? '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const m = byId.get(id);
    const name = m ? personDisplayName(m) : clean(a.role_at_date);
    if (!name) continue; // jamais d'UUID dans un document
    sortable.push({ name, position: m ? personPosition(m) : '', rank: crewRank(m) });
  }
  return sortCrewRows(sortable);
}

/** Groupe 0 = musicien, 1 = tout le reste (crew / tech / inconnu). */
const crewRank = (m?: PersonLike | null): number =>
  (clean(m?.category).toLowerCase() === 'musician' ? 0 : 1);

/** Tri commun aux deux sources : musiciens d'abord, puis A→Z par nom affiché. */
function sortCrewRows(rows: Array<CrewRow & { rank: number }>): CrewRow[] {
  rows.sort((x, y) => x.rank - y.rank || x.name.localeCompare(y.name, undefined, { sensitivity: 'base' }));
  return rows.map((r) => ({ name: r.name, position: r.position }));
}

/**
 * Les personnes du SHOW (« People on this show ») — équivalent de `crewRowsFromAssignments`
 * mais pour la vraie source : la sélection du rider du show (`active_musician_ids` ∪
 * `active_crew_ids`), et à défaut les cartes `base_role = main` du groupe (2026-11-03).
 *
 * ⚠️ Ne PAS confondre avec `date_assignments` (= le ROOMING) : un membre peut être sur la
 * route et n'avoir aucune chambre assignée, l'inverse est vrai aussi. BAND AND CREW =
 * « qui est là ce jour », pas « qui dort où ».
 */
export function crewRowsFromSelection(
  ids: Iterable<string | number> | null | undefined,
  members: Array<PersonLike & { id?: string | number | null }> | null | undefined,
  /** Rôles définis POUR CE SHOW (`rider_roles` : un rôle peut n'être vrai que sur une
   *  date). Priorité absolue sur la carte du groupe et sur le registre. */
  roleOverrides?: Record<string, string[]> | null,
): CrewRow[] {
  const list = members || [];
  const byId = new Map<string, PersonLike & { id?: string | number | null }>();
  for (const m of list) if (m && m.id != null) byId.set(String(m.id), m);
  const out: Array<CrewRow & { rank: number }> = [];
  const seen = new Set<string>();
  for (const raw of ids || []) {
    const id = String(raw ?? '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const m = byId.get(id);
    if (!m) continue; // id inconnu du registre : on n'invente pas de nom
    const name = personDisplayName(m);
    if (!name) continue;
    const override = (roleOverrides?.[id] || []).map((r) => String(r || '').trim()).filter(Boolean);
    out.push({
      name,
      position: override.length > 0 ? capitalizeFirst(override.join(', ')) : personPosition(m),
      rank: crewRank(m),
    });
  }
  return sortCrewRows(out);
}

/**
 * Trie des NOMS de personnes par ordre alphabétique, insensible aux accents et à
 * la casse (« CAVAZZANA » avant « LOCHER », « é » comme « e »).
 * Utilisé pour les passagers d'un vol : le document doit se lire comme un
 * boarding list, pas dans l'ordre d'insertion des `member_ids` (2026-11-03).
 */
export function sortPeopleNames(names: (string | null | undefined)[]): string[] {
  return names
    .map((n) => String(n ?? '').trim())
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }));
}
